import { cleanFullAddress } from 'utils/formatAddress';
import {
  FormSubmissionRequestFormType,
  FormSubmissionRequestState,
} from '@urbanx/agx.campaigns.client';
import clsx from 'clsx';
import { useEffect, useState, useCallback, useMemo, useContext, useRef } from 'react';
import {
  filterDisplayElementHideCondition,
  filterPageHideCondition,
  useDeepMemo,
  PageStatusType,
  SubmitType,
  addDividersBetweenElements,
  FormConfigType,
  PageStatus,
  DisplayElement,
} from '@urbanx/agx-ui-components';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { AuthContext } from '@urbanx/agx.auth.client';
import { FormType } from '@urbanx/agx.campaigns.client';
import { AgxButton, AgxSpacer } from '@urbanx/agx.ui-components2';
import { CampaignsApiContext } from '@urbanx/agx.campaigns.client';
import { FormOperationProvider, useFormOperationQueue } from './FormOperationContext';
import { FormEntry } from './FormEntry';
import { FormHeader } from './FormHeader/FormHeader';
import { updateFormErrors, updatePageStatuses, clearGeneratedFormDocuments } from './formReducer';
import { useElementRenderer } from 'hooks/useElementRenderer';
import { DesktopHeader } from './DesktopHeader/DesktopHeader';
import { DesktopSidebar } from './DesktopSidebar/DesktopSidebar';
import { useFormSettings } from 'hooks/useFormSettings';
import { useFormClearer } from 'hooks/useFormCleaner';
import GeneratingPage from './TransitionPages/GeneratingPage';
import GenerationStatus from './TransitionPages/GenerationStatus';
import FormButtons from './FormButtons/FormButtons';
import { delay } from 'utils/delay';
import { markCampaignsAsStale, reloadCampaigns } from '../campaigns/campaignsReducer';
import { useToaster } from 'hooks/useToaster';
import ErrorModal from 'helpers/ErrorModal';
import { usePreloadRenderer } from 'hooks/usePreloadRenderer';
import BeginCampaign from '../elements/BeginCampaign/BeginCampaign';
import { normalizeFormDates, useFormSaver } from 'hooks/useFormSaver';
import { submitMarketingPackage } from 'Api/Marketing/marketingApi';
import './form.scss';
import { useCampaignStaleMarker } from 'hooks/useCampaignStaleMarker';
import { haveAllGeneratedDocumentsReady } from './generatedDocumentsReady';
import { shouldStopForSubmissionErrors } from './shouldStopForSubmissionErrors';

const GenerationCompletionAction = {
  ExitForm: 'exit-form',
  GoToNextPage: 'go-to-next-page',
} as const;

type GenerationCompletionActionValue =
  (typeof GenerationCompletionAction)[keyof typeof GenerationCompletionAction];

export const Form = () => {
  const { campaignId, formId, selectedForm } = useAppSelector((state) => state.form);
  return (
    <FormContent
      key={JSON.stringify([campaignId, formId, selectedForm?.formType, selectedForm?.state])}
    />
  );
};

const FormContent = () => {
  const campaigns = useContext(CampaignsApiContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const proposalId = searchParams.get('proposalId');
  const proposalTemplateId = searchParams.get('proposalTemplateId');
  const proposalTemplateVersion = searchParams.get('proposalTemplateVersion');
  const agreementPreparation = searchParams.get('agreementPreparation');
  const { pathname } = useLocation();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [localPageStatuses, setLocalPageStatuses] = useState<PageStatus[]>([]);
  const { getAuthToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const clearFormData = useFormClearer();
  const { showErrorToast } = useToaster();
  const updateFormSettings = useFormSettings();
  const saveFormChanges = useFormSaver();
  const enqueue = useFormOperationQueue();
  const submissionPending = useRef(false);
  const [isSubmissionPending, setIsSubmissionPending] = useState(false);
  const markCampaignAsStale = useCampaignStaleMarker();

  const {
    selectedForm,
    formId,
    campaignId,
    formPages,
    formValues: formData,
    formErrors: errors,
    pageStatuses,
    generatedFormDocuments,
    generatedFormDocumentTransitionPending,
    generatedDocumentTypes,
    address: formAddress,
  } = useAppSelector((state) => state.form);

  const { formType, state, formConfigType } = selectedForm || {};

  const pages = useDeepMemo(() => {
    const visiblePages = filterPageHideCondition(formPages ?? [], formData);
    if (formType !== FormType.ListingProposal || agreementPreparation !== 'later')
      return visiblePages;

    return visiblePages.filter((page) =>
      ['Property Details', 'Vendor Details'].includes(page.name)
    );
  }, [agreementPreparation, formData, formPages, formType]);

  const pageKey = searchParams.get('pageKey');
  const [currentPageNum, setCurrentPageNum] = useState(() => {
    if (
      pageKey !== null &&
      formType === FormType.ListingProposal &&
      campaignId &&
      formId &&
      searchParams.get('campaignId') === campaignId &&
      searchParams.get('formId') === formId
    ) {
      return pages.findIndex((page) => page.name === pageKey);
    }
    return 0;
  });
  const previousPageNum = useRef(currentPageNum);
  const currentPage = useMemo(() => pages[currentPageNum], [pages, currentPageNum]);

  const {
    listingAddress,
    showBreadcrumbs,
    showListingDetails,
    displayTitle,
    displayTitleVisible,
    formBackgroundInverted,
    viewPreFill,
    growContent = false,
    noGap = false,
  } = useAppSelector((state) => state.formSettings);

  const [openErrorModal, setOpenErrorModal] = useState(false);

  const [generatingStatus, setGeneratingStatus] = useState(GenerationStatus.NotGenerating);
  const [generationCompletionAction, setGenerationCompletionAction] =
    useState<GenerationCompletionActionValue>();
  const [validatingForm, setValidatingForm] = useState(false);

  const isValidPreviousPage = (targetPageNumber: number) => {
    const pageIndex = pages.findIndex((page) => page?.name === currentPage?.name);
    if (targetPageNumber < pageIndex) return true;
    return false;
  };

  const isValidNextPage = (targetPageNumber: number) => {
    const currentPageIndex = pages.findIndex((page) => page.name === currentPage.name);
    if (targetPageNumber >= pages.length) return true;

    // if pageStatuses is null, move one page at a time
    if (pageStatuses === null || currentPageIndex + 1 === targetPageNumber) {
      return true;
    }

    const targetPageName = pages[targetPageNumber].name;
    const targetPageStatus = pageStatuses?.find((page) => page.pageName === targetPageName)?.status;

    // should be able to navigate to completed pages
    if (targetPageStatus === PageStatusType.Complete) {
      return true;
    }

    // target page is the next available page
    if (targetPageNumber > 0) {
      const oneBeforeTargetPageIndex = targetPageNumber - 1;
      const oneBeforeTargetPageName = pages[oneBeforeTargetPageIndex].name;
      const oneBeforeTargetPageStatus = pageStatuses?.find(
        (page) => page.pageName === oneBeforeTargetPageName
      )?.status;
      if (oneBeforeTargetPageStatus && oneBeforeTargetPageStatus === PageStatusType.Complete) {
        return true;
      }
    }

    return false;
  };

  const validateCurrentPage = async (targetPageNumber: number) => {
    setValidatingForm(true);
    const isPreviousPage = isValidPreviousPage(targetPageNumber);
    const isNextPage = isValidNextPage(targetPageNumber);
    await delay(30);
    const elementsWithErorr = document.getElementsByClassName('error');

    if (elementsWithErorr.length > 0) {
      elementsWithErorr[0].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    const fileIsUploading = document.getElementsByClassName('uploadProgress');
    if (fileIsUploading.length > 0) {
      fileIsUploading[0].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    // update the page status to complete if validation is success
    const existingPageStatus = localPageStatuses.filter(
      (page) => page.pageName !== currentPage.name
    );
    const pageStatus = {
      status: PageStatusType.Invalid,
      pageName: currentPage.name,
    };

    if (elementsWithErorr.length !== 0 && fileIsUploading.length !== 0) {
      pageStatus.status = PageStatusType.Invalid;
      setLocalPageStatuses([...existingPageStatus, pageStatus]);
      dispatch(
        updatePageStatuses({
          pageStatuses: [...existingPageStatus, pageStatus],
        })
      );
    }

    if (isNextPage && elementsWithErorr.length === 0 && fileIsUploading.length === 0) {
      pageStatus.status = PageStatusType.Complete;
      setLocalPageStatuses([...existingPageStatus, pageStatus]);
      dispatch(
        updatePageStatuses({
          pageStatuses: [...existingPageStatus, pageStatus],
        })
      );
    }

    return {
      isPreviousPage,
      isNextPage,
      status: elementsWithErorr.length === 0 && fileIsUploading.length === 0,
      pageStatuses: [...existingPageStatus, pageStatus],
    };
  };

  const updateCurrentPage = async (pageNum: number) => {
    if (pageNum >= 0 && pageNum < pages.length) {
      const validationResult = await validateCurrentPage(pageNum);
      if (validationResult.status) {
        setValidatingForm(false);

        if (validationResult.isPreviousPage || validationResult.isNextPage) {
          setCurrentPageNum(pageNum);
        }
      }
    }
  };

  const goToPreviousPage = async () => {
    const validationResult = await validateCurrentPage(currentPageNum - 1);
    if (validationResult.status) {
      // reset validation
      setValidatingForm(false);

      const nextPage = currentPageNum - 1;

      if (nextPage >= 0) {
        setCurrentPageNum(nextPage);
      }
    }
  };

  const goToNextPage = async (skipValidation = false) => {
    const validationResult = skipValidation
      ? undefined
      : await validateCurrentPage(currentPageNum + 1);

    if (skipValidation || validationResult?.status) {
      // reset validation
      setValidatingForm(false);

      const nextPage = currentPageNum + 1;

      if (nextPage < pages.length) {
        setCurrentPageNum(nextPage);
      }
    }
  };

  const goToFirstPage = () => {
    setCurrentPageNum(0);
  };

  const saveForm = async () => {
    if (!campaignId || !formId || !formType) throw new Error('Form identity is missing.');

    await saveFormChanges(formConfigType);
  };

  const renderElements = useElementRenderer({
    goToFirstPage,
    goToPreviousPage,
    clearValidation: () => setValidatingForm(false),
  });
  const renderPreloader = usePreloadRenderer();

  const saveAndContinue = async () => {
    if (saving || !campaignId || !formId) return;
    setSaving(true);
    setSaveError(undefined);
    try {
      const result = await validateCurrentPage(currentPageNum + 1);
      if (!result.status) return;
      setValidatingForm(false);
      await saveForm();
      if (proposalId) {
        navigate(`/proposals/${encodeURIComponent(proposalId)}`);
        return;
      }

      const setupParams = new URLSearchParams({ formId });
      if (proposalTemplateId) setupParams.set('proposalTemplateId', proposalTemplateId);
      if (proposalTemplateVersion)
        setupParams.set('proposalTemplateVersion', proposalTemplateVersion);
      navigate(`/proposals/setup/${encodeURIComponent(campaignId)}?${setupParams}`);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save proposal facts.');
    } finally {
      setSaving(false);
    }
  };

  const submitFormForReview = async () => {
    if (formType === FormType.ListingProposal) {
      await saveAndContinue();
      return;
    }
    if (!campaignId || !formId || !formType || !formConfigType) return;

    if (submissionPending.current) return;
    submissionPending.current = true;
    setIsSubmissionPending(true);
    try {
      return await enqueue(async () => {
        const result = await validateCurrentPage(currentPageNum + 1);
        if (result.status) {
          setValidatingForm(false);
          setGenerationCompletionAction(undefined);
          setGeneratingStatus(GenerationStatus.Generating);

          try {
            const token = await getAuthToken();

            if (!token) {
              showErrorToast('Failed to login');
              setGeneratingStatus(GenerationStatus.NotGenerating);
              return;
            }

            let errors: string[] = [];
            let submittedCampaignId: string | undefined;

            switch (formConfigType) {
              case FormConfigType.Campaign: {
                const normalizedFormData = normalizeFormDates(formData);
                const response = await campaigns.submitForm({
                  formType: FormSubmissionRequestFormType[formType],
                  formData: normalizedFormData,
                  formId: formId,
                  campaignId,
                });

                errors = response.errors ?? [];
                submittedCampaignId = response.campaignId;

                dispatch(updateFormErrors(errors));
                dispatch(markCampaignsAsStale());
                void markCampaignAsStale(campaignId).catch(console.error);
                dispatch(clearGeneratedFormDocuments());
                break;
              }
              case FormConfigType.Marketing: {
                const normalizedFormData = normalizeFormDates(formData);
                const data = await submitMarketingPackage(
                  token,
                  campaignId,
                  formId,
                  formType,
                  normalizedFormData
                );
                break;
              }
              default: {
                break;
              }
            }

            if (
              shouldStopForSubmissionErrors(errors, currentPage.submitType, submittedCampaignId)
            ) {
              setOpenErrorModal(true);
              setGenerationCompletionAction(undefined);
              setGeneratingStatus(GenerationStatus.NotGenerating);
              return;
            }

            if (currentPage.submitType === SubmitType.Send) {
              setGenerationCompletionAction(GenerationCompletionAction.ExitForm);
              setGeneratingStatus(GenerationStatus.Generated);
            }
          } catch (err: any) {
            showErrorToast(err.message);
            setGenerationCompletionAction(undefined);
            setGeneratingStatus(GenerationStatus.NotGenerating);
            return err.message;
          }
        }
      });
    } finally {
      submissionPending.current = false;
      setIsSubmissionPending(false);
    }
  };

  const exitForm = async (callSaveForm = true) => {
    if (callSaveForm && (campaignId || formId)) {
      try {
        await saveForm();
      } catch (error) {
        showErrorToast(error instanceof Error ? error.message : 'Failed to save form.');
        return;
      }
    }

    if (campaignId) void markCampaignAsStale(campaignId).catch(console.error);
    if (formType === FormType.ListingProposal && proposalId) {
      navigate(`/proposals/${encodeURIComponent(proposalId)}`);
      return;
    }
    clearFormData();
    navigate('/');
    dispatch(reloadCampaigns());
  };

  const handleGenerationComplete = () => {
    const completionAction = generationCompletionAction;
    setGenerationCompletionAction(undefined);

    if (completionAction === GenerationCompletionAction.ExitForm) {
      void exitForm(false);
      return;
    }

    setGeneratingStatus(GenerationStatus.NotGenerating);

    if (completionAction === GenerationCompletionAction.GoToNextPage) {
      void goToNextPage(true);
    }
  };

  const renderElementsWithDividers = (elements: DisplayElement[], validatingForm: boolean) => {
    return addDividersBetweenElements(
      renderElements(elements, validatingForm),
      true,
      currentPageNum < pages.length - 1
    );
  };

  useEffect(() => {
    if (
      pathname === '/form' &&
      formType === FormType.ListingProposal &&
      campaignId &&
      formId &&
      (searchParams.get('campaignId') !== campaignId || searchParams.get('formId') !== formId)
    ) {
      setSearchParams(
        { campaignId, formId, ...(proposalId ? { proposalId } : {}) },
        { replace: true }
      );
    }
  }, [pathname, formType, campaignId, formId, proposalId, searchParams, setSearchParams]);

  useEffect(() => {
    if (pageStatuses) setLocalPageStatuses(pageStatuses);
  }, [pageStatuses]);

  const pageChanged = useCallback(
    (pageNumber: number, saveChanges: boolean = true) => {
      updateFormSettings({
        displayTitleVisible: true,
      });

      if (generatingStatus === GenerationStatus.Generating)
        setGeneratingStatus(GenerationStatus.NotGenerating);
      if (pages != null && pages[pageNumber] != null) {
        const { invertedColours, componentManagedFormPage, hasBreadcrumbs, noGap } =
          pages[pageNumber];

        if (!componentManagedFormPage) {
          updateFormSettings({
            formBackgroundInverted: invertedColours,
            showBreadcrumbs: hasBreadcrumbs,
            displayTitle: undefined,
            noGap: noGap,
          });
        }
      }

      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      if (campaignId && pages && saveChanges) {
        void saveForm().catch((error: unknown) => {
          showErrorToast(error instanceof Error ? error.message : 'Failed to save form.');
        });
      }
    },
    [updateFormSettings, generatingStatus, setGeneratingStatus, pages, campaignId, saveForm]
  );

  useEffect(() => {
    // Mount and loaded-identity initialization update chrome, but only navigation saves facts.
    const navigated = previousPageNum.current !== currentPageNum;
    previousPageNum.current = currentPageNum;
    pageChanged(currentPageNum, navigated);
  }, [currentPageNum]);

  useEffect(() => {
    if (!generatedFormDocumentTransitionPending || !generatedFormDocuments) return;

    const allGeneratedDocumentsReady = haveAllGeneratedDocumentsReady(
      generatedDocumentTypes,
      generatedFormDocuments
    );

    if (allGeneratedDocumentsReady) {
      setGenerationCompletionAction(GenerationCompletionAction.GoToNextPage);
      setGeneratingStatus(GenerationStatus.Generated);
    }
  }, [generatedDocumentTypes, generatedFormDocumentTransitionPending, generatedFormDocuments]);

  const elements = useDeepMemo(
    () => filterDisplayElementHideCondition(currentPage?.elements, formData),
    [currentPage, formData]
  );

  useEffect(() => {
    if (!formType || !state) exitForm(false);
  }, [formType, state]);

  const formClasses = clsx(
    'agx-form',
    formBackgroundInverted && 'inverted',
    currentPage?.letComponentManageHorizontalPadding && 'noHorizontalPadding'
  );

  if (currentPageNum === -1) {
    return (
      <AgxSpacer padding="lg" gap="md">
        <p role="alert">The requested page “{pageKey}” is unavailable in this saved form.</p>
        <AgxButton
          id="proposal-form-unavailable-recovery"
          variant="hollow"
          text={proposalId ? 'Return to proposal' : 'Open form from the first page'}
          onClick={() => {
            if (proposalId) {
              navigate(`/proposals/${encodeURIComponent(proposalId)}`);
            } else {
              const params = new URLSearchParams(searchParams);
              params.delete('pageKey');
              setSearchParams(params, { replace: true });
            }
          }}
        />
      </AgxSpacer>
    );
  }

  if (generatingStatus !== GenerationStatus.NotGenerating) {
    return (
      <GeneratingPage
        generationStatus={generatingStatus}
        formType={formType}
        onComplete={handleGenerationComplete}
        submitType={currentPage?.submitType}
        state={state}
      />
    );
  }

  // This is here so that it fills the whole page when
  // breadcrumbs are not available
  const formPageClasses = clsx('agxFormPage', !showBreadcrumbs && 'noBreadcrumbs');

  const formBackgroundClasses = clsx('formBackground', !showBreadcrumbs && 'noBreadcrumbs');

  return (
    <div className={formBackgroundClasses}>
      <DesktopHeader onBackToCampaigns={exitForm} formId={formId} />
      {renderPreloader(pages)}
      <div className={formPageClasses}>
        {showBreadcrumbs && (
          <DesktopSidebar
            address={formData.PropertyAddress ?? formAddress}
            formTypeValue={formType}
            pages={pages}
            currentPage={currentPage}
            setPage={setCurrentPageNum}
            pageValidation={validateCurrentPage}
            updatePageValidationFlag={setValidatingForm}
          />
        )}
        <div className={!formId ? 'formContent formContentPropertySearch' : 'formContent'}>
          {displayTitleVisible && formId && currentPage && (
            <FormHeader
              showListingDetails={showListingDetails}
              listingAddress={listingAddress ?? cleanFullAddress(formAddress)}
              showBreadcrumbs={showBreadcrumbs}
              displayTitle={displayTitle}
              viewPreFill={viewPreFill}
              currentPage={currentPage}
              pages={pages}
              setPage={updateCurrentPage}
              saveForm={saveForm}
              formTypeValue={formType}
            />
          )}
          {!formId ? (
            <BeginCampaign id="BeginCampaign" />
          ) : (
            <AgxSpacer
              className={formClasses}
              padding={{ top: 'lg', right: 'xl', bottom: 'lg', left: 'xl' }}
            >
              <div
                className={clsx(
                  'agx-form-elements',
                  growContent && 'growContent',
                  noGap && 'noGap'
                )}
              >
                {renderElementsWithDividers(elements, validatingForm)}
              </div>
              {saveError && <p role="alert">{saveError}</p>}
              {currentPage &&
              formType === FormType.ListingProposal &&
              currentPageNum === pages.length - 1 ? (
                <AgxSpacer direction="horizontal" gap="md">
                  {currentPageNum > 0 && (
                    <AgxButton
                      id="proposal-form-back"
                      variant="hollow"
                      text="Back"
                      onClick={goToPreviousPage}
                      disabled={saving}
                    />
                  )}
                  <AgxButton
                    id="proposal-form-continue"
                    variant="primary"
                    text={
                      saving
                        ? 'Saving…'
                        : proposalId
                          ? 'Save and return to proposal'
                          : 'Save and continue'
                    }
                    onClick={saveAndContinue}
                    disabled={saving}
                  />
                </AgxSpacer>
              ) : (
                currentPage && (
                  <FormButtons
                    actionButtonType={currentPage.actionButtonType}
                    currentPageNum={currentPageNum}
                    totalPageNum={pages.filter((p) => p.hasBreadcrumbs).length}
                    goToPreviousPage={goToPreviousPage}
                    goToNextPage={() => goToNextPage(false)}
                    reviewForm={submitFormForReview}
                    submissionPending={isSubmissionPending}
                  />
                )
              )}
              {openErrorModal && (
                <ErrorModal
                  errorList={errors}
                  setShowModal={setOpenErrorModal}
                  title={
                    formType === FormType.ListingSubmission
                      ? 'Your listing submission is missing required fields'
                      : undefined
                  }
                  instruction={
                    formType === FormType.ListingSubmission
                      ? 'Complete required fields on these pages before submitting the listing:'
                      : undefined
                  }
                />
              )}
            </AgxSpacer>
          )}
        </div>
      </div>
    </div>
  );
};

const FormRoute = () => {
  const [params] = useSearchParams();
  return (
    <FormOperationProvider key={params.toString()}>
      <FormEntry>
        <Form />
      </FormEntry>
    </FormOperationProvider>
  );
};

export default FormRoute;
